// Central place for every cost, reward and progression curve, so the meta-game can be
// balanced without hunting numbers through the scenes (CLAUDE.md §5.4).
import type { Athlete, StatKey } from './types';

export const STAT_KEYS: StatKey[] = ['speed', 'power', 'agility', 'stamina', 'technique'];

// ── Training (the repeatable coin sink) ─────────────────────────────────────────────
// Each training level adds STAT_STEP points to one stat (permanent), up to MAX_TRAIN_LEVEL
// levels per stat. Cost rises steeply so a fully-trained athlete is a long-term project.
export const STAT_STEP = 3;
export const MAX_TRAIN_LEVEL = 6;
export const STAT_CAP = 100;

/** Coins to buy the NEXT level of a stat already trained `level` times (0-based). */
export function trainCost(level: number): number {
  return Math.round(40 * Math.pow(1.7, level)); // 40, 68, 116, 197, 335, 570
}

/** How many times a given stat has been trained on this athlete (0 if never). */
export function trainLevel(athlete: Athlete, stat: StatKey): number {
  return athlete.train?.[stat] ?? 0;
}

/** Cost of the next training level for this stat, or null if already maxed. */
export function nextTrainCost(athlete: Athlete, stat: StatKey): number | null {
  const lvl = trainLevel(athlete, stat);
  return lvl >= MAX_TRAIN_LEVEL ? null : trainCost(lvl);
}

// ── Fatigue (turns a dead field into a soft sink + reason to build a deep roster) ────
// Fatigue grows each race and recovers in real time while you're away. High fatigue gently
// lowers the athlete's effective stats, so a tired animal underperforms until it rests.
export const FATIGUE_PER_RACE = 18;
export const FATIGUE_RECOVER_PER_MIN = 0.6; // full recovery from 100 in ~2.8h
export const FATIGUE_MAX_PENALTY = 0.14; // at fatigue 100, stats are -14%

/** Performance multiplier (1 when fresh, down to 1-FATIGUE_MAX_PENALTY when exhausted). */
export function fatiguePenalty(fatigue: number): number {
  return 1 - (Math.max(0, Math.min(100, fatigue)) / 100) * FATIGUE_MAX_PENALTY;
}

/** Recover fatigue for time spent away (clamped to >= 0). */
export function recoverFatigue(fatigue: number, elapsedMs: number): number {
  const recovered = (elapsedMs / 60_000) * FATIGUE_RECOVER_PER_MIN;
  return Math.max(0, fatigue - recovered);
}

/** Fatigue-level key for the UI (translated via i18n: fat.fresh / fat.fit / fat.tired / fat.exhausted). */
export function fatigueKey(fatigue: number): 'fresh' | 'fit' | 'tired' | 'exhausted' {
  if (fatigue < 20) return 'fresh';
  if (fatigue < 45) return 'fit';
  if (fatigue < 70) return 'tired';
  return 'exhausted';
}

// ── Consumable boosts (one-race coin sink + the rewarded-ad hook) ────────────────────
// Deliberately MILD and capped: a boost helps you reach your potential, it never raises the
// natural ceiling. So boosted runs count on the leaderboard fairly — the top is still decided
// by maxed stats + tap skill (which anyone can reach), and a tired athlete on an energy drink
// can at best match its own fresh baseline, never beat it.
export type BoostId = 'energy' | 'turbo';

export interface BoostDef {
  id: BoostId;
  emoji: string;
  name: string;
  desc: string;
  price: number;
}

export const BOOSTS: BoostDef[] = [
  { id: 'energy', emoji: '🥤', name: 'Energy drink', desc: 'Corri da fresco (fatica annullata)', price: 30 },
  { id: 'turbo', emoji: '⚡', name: 'Turbo', desc: '+6 a tutte le stat (cap 100)', price: 80 },
];

export function getBoost(id: BoostId): BoostDef | undefined {
  return BOOSTS.find((b) => b.id === id);
}

/** Turn a chosen boost into the modifiers effectiveStats understands. */
export function boostOpts(id: BoostId | undefined): EffectiveOpts | undefined {
  if (id === 'energy') return { freshen: true };
  if (id === 'turbo') return { bonusAll: 6 };
  return undefined;
}

// ── Effective stats ─────────────────────────────────────────────────────────────────
export interface EffectiveOpts {
  /** Ignore fatigue for this computation (energy-drink boost). */
  freshen?: boolean;
  /** Flat bonus added to every stat before the cap (turbo boost). */
  bonusAll?: number;
}

/**
 * The stats a discipline should actually use: base (from the roster definition) + trained
 * bonus + any one-race boost, clamped to STAT_CAP, then scaled by the fatigue penalty. Returns
 * a fresh object — never mutates the athlete — so persisted base stats stay clean and reconcile
 * can still rebalance them. The cap is what keeps boosts from inflating the leaderboard ceiling.
 */
export function effectiveStats(athlete: Athlete, opts?: EffectiveOpts): Record<StatKey, number> {
  const fatigue = opts?.freshen ? 0 : athlete.fatigue;
  const penalty = fatiguePenalty(fatigue);
  const bonus = opts?.bonusAll ?? 0;
  const out = {} as Record<StatKey, number>;
  for (const k of STAT_KEYS) {
    const raised = Math.min(STAT_CAP, athlete.stats[k] + trainLevel(athlete, k) * STAT_STEP + bonus);
    out[k] = Math.round(raised * penalty);
  }
  return out;
}
